import { uniq } from 'es-toolkit';
import React from 'react';
import { Box, Checkbox, Divider, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';

import { HelpButton } from '@paragon/client-tools';
import { UserRole, UserRoleSet } from '@paragon/contract-library';

import {
  addPermissionToRoleSet,
  getAllKnownPermissions,
  getPermissionsFromRoleSet,
  getPermissionsGrantedByRole,
  getUnassociatedPermissions,
  removePermissionFromRoleSet,
  setRoleInRoleSet,
  USER_ROLES,
} from '../../roles';
import { PermissionCategory } from './PermissionCategory';

interface Props {
  readonly showOtherPermissions?: boolean;

  readonly value: UserRoleSet;
  readonly onChange: (value: UserRoleSet) => void;
}

interface State {
  readonly showAllPermissions: boolean;
  readonly permissionAnarchy: boolean;
}

export class UserRoleEditor extends React.Component<Props, State> {
  state: State = {
    showAllPermissions: false,
    permissionAnarchy: false,
  };

  onToggleShowAllPermissions = () => {
    this.setState((state) => ({ showAllPermissions: !state.showAllPermissions, permissionAnarchy: false }));
  };

  onTogglePermissionAnarchy = () => {
    this.setState((state) => ({ permissionAnarchy: !state.permissionAnarchy }));
  };

  onSetRole = (role: UserRole) => {
    this.props.onChange(setRoleInRoleSet(this.props.value, role));
  };

  onSetPermission = (permission: string, checked: boolean) => {
    this.props.onChange(
      checked
        ? addPermissionToRoleSet(this.props.value, permission)
        : removePermissionFromRoleSet(this.props.value, permission),
    );
  };

  renderPermissionsCheckboxes(showAll: boolean) {
    const heldPermissions = new Set(getPermissionsFromRoleSet(this.props.value));
    const permissionsFromRole = this.props.value.role ? getPermissionsGrantedByRole(this.props.value.role) : [];

    const recognizedPermissions = getAllKnownPermissions();
    const allEncounteredPermissions = uniq([...recognizedPermissions, ...this.props.value.whitelist]);

    const displayPermissions = showAll
      ? allEncounteredPermissions
      : uniq([...getUnassociatedPermissions(), ...this.props.value.whitelist]);

    return displayPermissions.toSorted().map((permission) => (
      <FormControlLabel
        key={permission}
        label={
          <Box component="span" sx={recognizedPermissions.includes(permission) ? null : { color: 'error.main' }}>
            {permission}
          </Box>
        }
        control={
          <Checkbox
            onChange={(event) => this.onSetPermission(permission, event.target.checked)}
            disabled={
              heldPermissions.has(permission) &&
              permissionsFromRole.includes(permission) &&
              !this.state.permissionAnarchy
            }
            checked={heldPermissions.has(permission)}
          />
        }
      />
    ));
  }

  render() {
    return (
      <>
        <PermissionCategory label="Roles">
          <RadioGroup
            sx={{ my: 1, mx: 0 }}
            value={this.props.value.role}
            onChange={(event) => this.onSetRole(event.target.value as UserRole)}
          >
            {Object.keys(USER_ROLES).map((role) => (
              <FormControlLabel key={role} label={role} value={role} control={<Radio color="secondary" />} />
            ))}
          </RadioGroup>
        </PermissionCategory>

        {this.props.showOtherPermissions && (
          <PermissionCategory label="Other Permissions">
            <FormControlLabel
              label={
                <>
                  Show All
                  <HelpButton>
                    <Typography variant="body2">
                      Checking this box will display every permission that a user can potentially have. Permissions that
                      are granted by the user&apos;s role can&apos;t be manually removed, unless anarchy is enabled.
                    </Typography>
                    <br />

                    <Typography variant="body2">
                      If a permission is red, it doesn&apos;t exist in the current version of our software and was
                      likely added manually or was/will be relevant in a different version.
                    </Typography>
                  </HelpButton>
                </>
              }
              control={
                <Checkbox
                  color="default"
                  checked={this.state.showAllPermissions}
                  onChange={this.onToggleShowAllPermissions}
                />
              }
            />

            {this.state.showAllPermissions && (
              <FormControlLabel
                label="Permission Anarchy"
                control={
                  <Checkbox
                    color="error"
                    checked={this.state.permissionAnarchy}
                    onChange={this.onTogglePermissionAnarchy}
                  />
                }
              />
            )}

            <Divider />

            {this.renderPermissionsCheckboxes(this.state.showAllPermissions)}
          </PermissionCategory>
        )}
      </>
    );
  }
}
